import React, { forwardRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";

/**
 * Reusable ReCAPTCHA v2 Component
 * @param {function} onChange - Callback when CAPTCHA is verified
 * @param {React.Ref} ref - Reference to access ReCAPTCHA methods (e.g., reset)
 */
const ReCaptchaComponent = forwardRef(({ onChange, onExpired, theme = "light" }, ref) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  // Default to enabled - only disable if explicitly set to 'false'
  const isRecaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED !== 'false';

  // Auto-bypass reCAPTCHA if disabled in environment
  useEffect(() => {
    if (!isRecaptchaEnabled && onChange) {
      // Provide a bypass token when reCAPTCHA is disabled
      onChange('recaptcha-bypass-token');
    }
  }, [isRecaptchaEnabled, onChange]);

  // If reCAPTCHA is disabled, don't render the widget
  if (!isRecaptchaEnabled) {
    console.info('reCAPTCHA is disabled in this environment');
    return null;
  }

  if (!siteKey) {
    console.error("reCAPTCHA site key is not defined in environment variables");
    return null;
  }


  const handleExpired = () => {
    console.warn("reCAPTCHA expired - user needs to verify again");
    if (onExpired) {
      onExpired(); // Call parent's expiration handler
    }
  };

  const handleError = () => {
    console.error("reCAPTCHA error occurred");
    // Auto-reset on error
    if (ref?.current) {
      ref.current.reset();
    }
  };

  return (
    <div className="recaptcha-wrapper mb-3">
      <ReCAPTCHA
        ref={ref}
        sitekey={siteKey}
        theme={theme}
        onChange={onChange}
        onErrored={handleError}
        onExpired={handleExpired}
      />
    </div>
  );
});

ReCaptchaComponent.displayName = "ReCaptchaComponent";

export default ReCaptchaComponent;
